import { useState, useEffect, useCallback, useRef } from 'react';

import { useProgress } from '@hydra/common/customHooks';
import { useErrorContext } from '@hydra/common/context';

import { getReviewStatus } from '@hydra/utils/reviewApi';
import { sendEvent } from '@hydra/utils/gtm';

const MAX_POLLING_DELAY = 5000;
const TIMEOUT_PAGE = 60000;

const delay = t => new Promise(resolve => setTimeout(resolve, t));

const getDelayTime = attempts => {
  if (attempts === 0) {
    return attempts;
  } else if (attempts === 1) {
    return MAX_POLLING_DELAY;
  } else {
    return Math.min(MAX_POLLING_DELAY, (attempts - 1) * 1000);
  }
};

const useReviewGeneration = ({ generationId, startTime, noRedirect = false }) => {
  const { setError } = useErrorContext();
  const [timesPolled, setTimesPolled] = useState(0);
  const timeoutId = useRef();
  const { progress, endProgress } = useProgress({ delay: TIMEOUT_PAGE / 100 });

  const stopProgress = useCallback(() => {
    endProgress();
    clearTimeout(timeoutId.current);
  }, [endProgress]);

  useEffect(() => {
    timeoutId.current = setTimeout(() => setError({ statusCode: 500 }), TIMEOUT_PAGE);
    return () => clearTimeout(timeoutId.current);
  }, [setError]);

  const handleRefreshEnd = useCallback(async () => {
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.delete('generationId');
    window.location.href = currentUrl;
  }, []);

  useEffect(() => {
    const getReviewPollingStatus = async () => {
      await delay(getDelayTime(timesPolled));
      const { status, error, resolve } = await getReviewStatus(generationId);
      if (error) {
        stopProgress();
        return setError({ statusCode: 500, resolve });
      } else if (status === 'ended') {
        stopProgress();
        sendEvent('review-loading-end', { duration: (new Date() - startTime.current) / 1000 });
        if (noRedirect) { return; }
        return handleRefreshEnd();
      } else {
        return setTimesPolled(t => t + 1);
      }
    };
    getReviewPollingStatus();
  }, [generationId, handleRefreshEnd, stopProgress, timesPolled, startTime, setError, noRedirect]);

  return { progress };
};

export default useReviewGeneration;
